import type { LanguageCode, RouteFeature, UnitSystem } from '../types';

export interface RouteSummaryModel {
  destination: string;
  remainingDistance: string;
  arrival: string;
  estimateSource: string | null;
}

const labels: Record<LanguageCode, {
  toward: (destination: string) => string;
  remaining: (distance: string) => string;
  around: (time: string) => string;
  unavailable: string;
  live: string;
  planned: string;
}> = {
  EN: { toward: (value) => `Toward ${value}`, remaining: (value) => `${value} remaining`, around: (value) => `Around ${value}`, unavailable: 'Arrival unavailable', live: 'Traffic-adjusted estimate', planned: 'Estimated from planned duration' },
  DE: { toward: (value) => `Richtung ${value}`, remaining: (value) => `${value} verbleibend`, around: (value) => `Gegen ${value}`, unavailable: 'Ankunft nicht verfügbar', live: 'Verkehrsangepasste Schätzung', planned: 'Schätzung aus geplanter Dauer' },
  ES: { toward: (value) => `Hacia ${value}`, remaining: (value) => `${value} restantes`, around: (value) => `Sobre las ${value}`, unavailable: 'Llegada no disponible', live: 'Estimación ajustada al tráfico', planned: 'Estimación según la duración prevista' },
  IT: { toward: (value) => `Verso ${value}`, remaining: (value) => `${value} rimanenti`, around: (value) => `Verso le ${value}`, unavailable: 'Arrivo non disponibile', live: 'Stima aggiornata con il traffico', planned: 'Stima dalla durata pianificata' },
  FR: { toward: (value) => `Vers ${value}`, remaining: (value) => `${value} restantes`, around: (value) => `Vers ${value}`, unavailable: 'Arrivée indisponible', live: 'Estimation ajustée au trafic', planned: 'Estimation selon la durée prévue' }
};

const languageLocales: Record<LanguageCode, string> = { EN: 'en-GB', DE: 'de-DE', ES: 'es-ES', IT: 'it-IT', FR: 'fr-FR' };

function routeDistanceKm(route: RouteFeature): number {
  const value = Number(route.properties.distance_km_precise ?? route.properties.distance_km ?? 0);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function destinationForRoute(route: RouteFeature): string {
  const marker = route.properties.markers?.at(-1)?.label.trim();
  return route.properties.destination_label?.trim() || marker || route.properties.short_name || route.properties.name;
}

function formatDistance(km: number, units: UnitSystem): string {
  if (units === 'imperial') return `${Math.round(km * 0.621371)} mi`;
  return `${Math.round(km)} km`;
}

export function buildRouteSummary(
  route: RouteFeature,
  progressM: number | null,
  liveTravelTimeS: number | undefined,
  language: LanguageCode,
  units: UnitSystem,
  nowMs = Date.now()
): RouteSummaryModel {
  const text = labels[language];
  const totalKm = routeDistanceKm(route);
  const safeProgressKm = Number.isFinite(progressM) ? Math.max(0, Math.min(totalKm, Number(progressM) / 1_000)) : 0;
  const fractionRemaining = totalKm > 0 ? Math.max(0, (totalKm - safeProgressKm) / totalKm) : 0;
  const remainingKm = Math.max(0, totalKm - safeProgressKm);
  const liveSeconds = Number(liveTravelTimeS);
  const plannedSeconds = Number(route.properties.duration_h) * 3_600;
  const hasLive = Number.isFinite(liveSeconds) && liveSeconds > 0;
  const hasPlanned = Number.isFinite(plannedSeconds) && plannedSeconds > 0;
  const totalSeconds = hasLive ? liveSeconds : hasPlanned ? plannedSeconds : null;
  const arrival = totalSeconds === null
    ? text.unavailable
    : text.around(new Intl.DateTimeFormat(languageLocales[language], { hour: '2-digit', minute: '2-digit' }).format(new Date(nowMs + totalSeconds * fractionRemaining * 1_000)));

  return {
    destination: text.toward(destinationForRoute(route)),
    remainingDistance: text.remaining(formatDistance(remainingKm, units)),
    arrival,
    estimateSource: hasLive ? text.live : hasPlanned ? text.planned : null
  };
}

export function RouteSummary({ summary }: { summary: RouteSummaryModel }) {
  return (
    <section className="route-summary" aria-label={summary.destination}>
      <strong>{summary.destination}</strong>
      <span>{summary.remainingDistance}</span>
      <span>{summary.arrival}</span>
      {summary.estimateSource ? <small>{summary.estimateSource}</small> : null}
    </section>
  );
}
